import React, { useEffect, useRef, useState } from 'react';
import { IconMinus } from '@americanexpress/dls-icons';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { useUpdateView } from '@americanexpress/data-manager-utils';
import { useReactlytics } from '@americanexpress/reactlytics';
import { ContinuousLinearTracker } from '@americanexpress/dls-react';
import styles from '../styles/CreateWishlistModal.scss';
import { getCityAndLocation } from '../utils/getCityAndLocation';
import { DEFAULT_ANALYTICS_PAGE_VIEW, MODULE_NAME } from '../utils/constants';

export const CreateWishlistModal = ({
  setCreateModalIsOpen, saveSelectedHotels, isSaveLoading,
  isSaveSuccess,
}) => {
  const intl = useIntl();
  const updateView = useUpdateView();
  const { track } = useReactlytics();
  const selectedHotels = useSelector((state) => state.getIn(['modules', MODULE_NAME, 'selectedHotels'])).toJS();
  const gridRef = useRef(null);
  const visibleRef = useRef(new Set());
  const [apiProgress, setApiProgress] = useState(0);

  useEffect(() => {
    updateView({
      viewId: `${DEFAULT_ANALYTICS_PAGE_VIEW}Confirmation`,
    });
    track({
      event: {
        identifier: 'axp - WishlistBuilder - Confirmation',
        element: 'ConfirmationPage',
        type: 'impression',
      },
    });
  }, [updateView, track]);

  useEffect(() => {
    const root = gridRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const hotelId = entry.target.getAttribute('data-hotel-id');
          if (!hotelId) return;

          const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.75;

          if (isVisible && !visibleRef.current.has(hotelId)) {
            visibleRef.current.add(hotelId);

            track({
              event: {
                identifier: 'axp - WishlistBuilder - Confirmation',
                element: `PropertyTile-${hotelId}`,
                type: 'impression',
              },
            });
          }

          if (!isVisible) {
            visibleRef.current.delete(hotelId);
          }
        });
      },
      {
        root,
        threshold: [0, 0.75, 1],
      }
    );

    root.querySelectorAll('[data-hotel-id]').forEach((node) => {
      observer.observe(node);
    });

    // eslint-disable-next-line consistent-return -- observer disconnect
    return () => observer.disconnect();
  }, [track, selectedHotels]);

  useEffect(() => {
    if (!isSaveLoading) return;

    const start = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - start;

      const value = 92 * (1 - Math.exp(-elapsed / 2500));

      setApiProgress(Math.min(value, 92));
    }, 16);

    // eslint-disable-next-line consistent-return -- clear interval
    return () => clearInterval(interval);
  }, [isSaveLoading]);

  useEffect(() => {
    if (!isSaveSuccess) return;

    setApiProgress(100);
  }, [isSaveSuccess]);

  const closeCreateModal = () => {
    track({
      event: {
        identifier: 'axp - WishlistBuilder - Confirmation',
        element: 'Exit',
        type: 'click',
      },
    });
    setCreateModalIsOpen(false);
  };

  return (
    <>
      <motion.div
        className={styles.popupOverlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.1 } }}
        exit={{ opacity: 0 }}
        onClick={() => setCreateModalIsOpen(false)}
      />
      <motion.div
        className={styles.popupContent}
        data-testid="popupModal"
        role="dialog"
        aria-modal="true"
        aria-label="create wishlist modal"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        drag="y"
        dragElastic={0.2}
        dragConstraints={{ top: 0 }}
        onDragEnd={(e, info) => {
          if (info.offset.y > 100 || info.velocity.y > 100) {
            setCreateModalIsOpen(false);
          }
        }}
      >
        <div
          className="flex flex-column width-full height-full text-align-center position-relative"
        >
          <div
            className="position-absolute position-absolute-tr"
          >
            <button
              type="button"
              style={{ height: '28px', width: '28px' }}
              onClick={closeCreateModal}
            >
              <IconMinus
                color="brand"
                size="md"
                isFilled={false}
                title="Close create wishlist popup"
                titleId="close-create-modal"
              />
            </button>
          </div>
          <div
            className="heading-3"
            style={{
              fontSize: '1.25rem',
              padding: '.625rem 0',
            }}
          >{intl.formatMessage({ id: 'createModal.title' })}
          </div>
          <div
            ref={gridRef}
            className={styles.grid}
            /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable */
            tabIndex={0}
            aria-label="Selected hotels"
          >
            {selectedHotels.map((hotel) => {
              const {
                hotelId, address, name, media,
              } = hotel;
              const { title, url } = media[0] || {};

              const { city, location } = getCityAndLocation(address);
              return (
                <div
                  key={hotelId}
                  data-hotel-id={hotelId}
                  className="text-align-left pad-2-t"
                >
                  <div
                    className="flex flex-align-center radius-container pad-1-b"
                    style={{ aspectRatio: '1/1' }}
                  >
                    <img
                      src={url}
                      className="radius-container"
                      alt={title}
                      style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                      data-testid="hotel-image"
                    />
                  </div>
                  <div
                    className={`${styles.hotelPopupName} body-small-bold`}
                  >
                    {name}
                  </div>
                  <div className={`${styles.locationGrayFont} body-xsmall`}>
                    {city}, {location}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={styles.popupFooterContainer}>
            <div style={{ height: '62px' }} className="flex flex-justify-center flex-align-center">
              {(isSaveLoading || isSaveSuccess) && (
              <ContinuousLinearTracker
                className={`${styles.linearTracker} width-full margin-1-b`}
                variant="minimal"
                value={apiProgress}
              />
              )}
              {!isSaveSuccess && !isSaveLoading && (
              <button
                type="button"
                data-testid="saveWishlistButton"
                className={`${styles.completeWishlistButton} heading-2`}
                onClick={saveSelectedHotels}
              >
                {intl.formatMessage({ id: 'createModal.saveWishlistButton' })}
              </button>
              )}
            </div>
            <div className={`${styles.popupDisclaimer} body-xsmall`}>
              {intl.formatMessage({ id: 'createModal.terms' })}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

CreateWishlistModal.propTypes = {
  setCreateModalIsOpen: PropTypes.func.isRequired,
  saveSelectedHotels: PropTypes.func.isRequired,
  isSaveLoading: PropTypes.bool.isRequired,
  isSaveSuccess: PropTypes.bool.isRequired,
};
